import React, { useState } from 'react'
import { getSelectGoodsCls as cls } from '../select-goods'

import '../select-goods.scss'
import { Goods } from '@/model/types/goods'
import { Tooltip, Image } from 'antd'
import WordHighLight from '@/components/word-highlight/word-highlight'
import { selectGoodStore } from '../model'
import Table, { ColumnsType } from 'antd/lib/table'
import { PricesPopper } from '../../prices/prices-popper/prices-popper'
import { intervalPrice } from '@/utils/goods'
import { Row } from '@/components/layout/layout'
import Iconfont from '@/components/iconfont/iconfont'
import { WithScrollLoad } from '@/utils/utils-widget'
import { $post } from '@/api'
import { IPagination } from '@/model/types/common'
import { useRequest, useUpdateEffect } from 'ahooks'
import { observer } from 'mobx-react'
import { getQiniuImgUrl } from '@/config/qiniu'

interface GoodsTableList {
    list: Goods[]
    pagination: IPagination
}

interface SelectGoodsTableProps {
    filterFn?: (good: Goods) => boolean
}

const SelectGoodsTable: React.FC<SelectGoodsTableProps> = observer(props => {
    const column: ColumnsType<Goods> = [
        {
            title: '图片',
            dataIndex: 'img_url',
            width: 70,
            render: val => (
                <div className={cls('good-img')}>
                    <Image width={50} height={50} src={getQiniuImgUrl(val)} />
                </div>
            ),
        },
        {
            title: '商品名',
            dataIndex: 'name',
            key: 'name',
            ellipsis: {
                showTitle: true,
            },
            width: 156,
            render: (value, record) => (
                <Tooltip placement="topLeft" title={value}>
                    <div
                        className="text-ellipsis-one"
                        style={{
                            maxWidth: '146px',
                        }}
                    >
                        {!!record.goods_package.length && <div className={cls('package')}>套</div>}
                        <WordHighLight keyword={selectGoodStore.keyWord} word={value}></WordHighLight>
                    </div>
                </Tooltip>
            ),
        },
        {
            title: '所属类目',
            key: 'category',
            dataIndex: 'category',
            render: category => category.name || '其他类目',
        },
        {
            title: '销量',
            dataIndex: 'amount_sold',
            key: 'amount_sold',
            render: (amount_sold, record) => `${amount_sold}${record.stock_unit.name}`,
        },
        {
            title: '库存',
            dataIndex: 'stock',
            key: 'stock',
            render: (val, record) => `${val}${record.stock_unit.name}`,
        },
        {
            title: '售价',
            dataIndex: 'pricing_methods',
            key: 'pricing_methods',
            align: 'right',
            render: (pricingMethod, record) => (
                <div>
                    {record.pricing_methods.length > 1 || record.pricing_methods[0].sale_prices.length > 1 ? (
                        <Tooltip
                            title={() => (
                                <PricesPopper
                                    stockUnit={record.stock_unit}
                                    pricingMethod={pricingMethod}
                                ></PricesPopper>
                            )}
                        >
                            <div data-cursor-pointer>
                                ¥{intervalPrice(record.pricing_methods).min}~{intervalPrice(record.pricing_methods).max}
                            </div>
                        </Tooltip>
                    ) : (
                        <div>
                            ¥{record.pricing_methods[0].sale_prices[0].sale_price}/
                            {record.pricing_methods[0].sale_prices[0].sale_unit.name}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: '上架',
            key: 'scene',
            width: 70,
            render: (value, record) => (
                <Row>
                    {record.status === 0 || record.on_mall ? (
                        <React.Fragment>
                            <span className={cls('icon')}>
                                {record.on_mall && <Iconfont name="icon2shangjiaweishangcheng" size={20} />}
                            </span>
                            <span>
                                {record.status === 0 && (
                                    <Iconfont
                                        name="icon2shangjiashouyintai"
                                        size={20}
                                        style={{
                                            marginLeft: '8px',
                                        }}
                                    />
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        <span>
                            <Iconfont name="iconweishangjia1" size={35}></Iconfont>
                        </span>
                    )}
                </Row>
            ),
        },
    ]

    const requestHandle = useRequest<
        GoodsTableList,
        {
            goods_list: Goods[]
            pagination: IPagination
        }
    >(
        val => {
            return (
                // 在分组未完成加载前不加载表格数据
                selectGoodStore.activeId
                    ? $post({
                          url: '/goods/getgoods',
                          params: {
                              // 如果当前是否为搜索
                              group_ids:
                                  selectGoodStore.activeId && !selectGoodStore.keyWord.trim()
                                      ? [selectGoodStore.activeId]
                                      : void 0,
                              secondary_group_id: !selectGoodStore.keyWord.trim() ? selectGoodStore.secondId : 0,
                              limit: 20,
                              page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                              keyword: selectGoodStore.keyWord ? selectGoodStore.keyWord : void 0,
                          },
                      })
                    : new Promise(() => {})
            )
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.goods_list,
                    pagination: data.pagination,
                }
            },
            refreshDeps: [selectGoodStore.activeId, selectGoodStore.secondId, selectGoodStore.keyWord],
        },
    )

    const [originKeys, setOriginKeys] = useState<number[]>([])
    const [originGoods, setOriginGoods] = useState<Goods[]>([])

    useUpdateEffect(() => {
        // 缓存上一个分组选中的商品
        setOriginKeys(selectGoodStore.selectKey)
        setOriginGoods(selectGoodStore.selectList)
    }, [selectGoodStore.activeId, selectGoodStore.secondId, selectGoodStore.keyWord])

    return (
        <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
            <Table
                rowKey={record => record.id}
                loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectGoodStore.selectKey,
                    onChange: (selectedRowKeys, selectList) => {
                        selectGoodStore.setSelectKey([...originKeys, ...(selectedRowKeys as number[])])
                        selectGoodStore.setSelectList([
                            ...originGoods,
                            ...selectList.filter(list => !originGoods.map(i => i.id).includes(list.id)),
                        ])
                    },
                    getCheckboxProps: record => ({
                        // 禁止情况： 1. 未上架 2. 套餐商品
                        disabled: !!record.goods_package.length || (props.filterFn && props.filterFn(record)),
                    }),
                }}
                bordered
                pagination={false}
                columns={column}
                dataSource={requestHandle.data?.list}
                scroll={{ x: 'max-content', y: 350 }}
            />
        </WithScrollLoad>
    )
})

export default SelectGoodsTable
